import type {
  Memory,
  MemoryInput,
  MemoryKind,
  MemoryScope,
  MemoryStatus,
  MemoryValidity,
} from "../types/MemoryTypes";

/**
 * Para onde a pessoa pode levar uma memória na edição: o próprio escopo, o pessoal, o
 * workspace aberto (se houver) e a organização (só admin). A api confere de novo — isto
 * só evita oferecer na tela o que ela recusaria.
 */
export function moveScopes(current: MemoryScope, options: { workspaceId?: string | null; isAdmin: boolean }): MemoryScope[] {
  const scopes: MemoryScope[] = ["user"];
  if (options.workspaceId) scopes.push("workspace");
  if (options.isAdmin) scopes.push("organization");
  if (!scopes.includes(current)) scopes.push(current);
  return scopes;
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** A partir daqui a validade aparece como "vence em breve". */
const EXPIRING_SOON_MS = 2 * DAY_MS;

export type ValidityState = "permanent" | "valid" | "expiringSoon" | "expired";

/** Como a validade de uma memória aparece na tela. */
export function validityState(memory: Pick<Memory, "expires_at" | "status">, now = new Date()): ValidityState {
  if (memory.status === "expired") return "expired";
  if (!memory.expires_at) return "permanent";
  const left = new Date(memory.expires_at).getTime() - now.getTime();
  if (left <= 0) return "expired";
  return left <= EXPIRING_SOON_MS ? "expiringSoon" : "valid";
}

export interface MemoryFilters {
  query: string;
  kinds: MemoryKind[];
  /** Vazio = todos os estados. */
  statuses: MemoryStatus[];
}

/** Busca sem acento e sem caixa, como os outros filtros de lista. */
function normalize(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function filterMemories(memories: Memory[], filters: MemoryFilters): Memory[] {
  const terms = normalize(filters.query.trim()).split(/\s+/).filter(Boolean);
  return memories.filter((memory) => {
    if (filters.statuses.length > 0 && !filters.statuses.includes(memory.status)) return false;
    if (filters.kinds.length > 0 && !filters.kinds.includes(memory.kind)) return false;
    if (terms.length === 0) return true;
    const text = normalize(memory.content);
    return terms.every((term) => text.includes(term));
  });
}

/** A validade de uma memória existente, para abrir o formulário de edição. */
export function validityOf(memory: Pick<Memory, "expires_at">): { validity: MemoryValidity; customDate?: string } {
  if (!memory.expires_at) return { validity: "permanent" };
  return { validity: "custom", customDate: memory.expires_at.slice(0, 10) };
}

/** Se a validade no formulário é outra que a de quando ele abriu. */
export function validityChanged(
  initial: Pick<MemoryInput, "validity" | "customDate">,
  current: Pick<MemoryInput, "validity" | "customDate">,
): boolean {
  if (initial.validity !== current.validity) return true;
  return current.validity === "custom" && initial.customDate !== current.customDate;
}

/**
 * O corpo que a api espera para a validade escolhida. Data customizada vale até o fim
 * do dia escolhido, no fuso de quem escolheu.
 */
export function validityPayload(
  input: Pick<MemoryInput, "validity" | "customDate">,
  options: { editing: boolean } = { editing: false },
): Record<string, unknown> {
  switch (input.validity) {
    case "permanent":
      return options.editing ? { permanent: true } : {};
    case "1d":
      return { ttl_days: 1 };
    case "7d":
      return { ttl_days: 7 };
    case "30d":
      return { ttl_days: 30 };
    case "custom": {
      if (!input.customDate) return {};
      const end = new Date(`${input.customDate}T23:59:59`);
      return { expires_at: end.toISOString() };
    }
  }
}

/** Uma data customizada no passado não forma validade. */
export function isValidCustomDate(date: string | undefined, now = new Date()): boolean {
  if (!date) return false;
  const end = new Date(`${date}T23:59:59`);
  return !Number.isNaN(end.getTime()) && end.getTime() > now.getTime();
}

/** Quem gravou: a pessoa, ou o fluxo quando não há pessoa. */
export function authorLabel(memory: Pick<Memory, "author" | "source">): string | null {
  if (memory.author) return memory.author.full_name || memory.author.email;
  return null;
}
